import { TextField } from "@clockwise/design-system";
import { Search } from "@clockwise/design-system/icons";
import { Profile } from "@clockwise/web-commons/src/util/profile.util";
import { cloneDeep, isEmpty } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { TrackingEvents, track } from "../../util/analytics.util";
import { BulkInviteResultRow } from "./BulkInviteResultRow";
import { InviteSearchResultRow } from "./InviteSearchResultRow";
import { useInviteSearch } from "./hooks/useInviteSearch";
import { BulkInviteEvent, BulkInviteEventMap } from "./types";
import { dedupeInviteSearchPersons } from "./utils/dedupeInviteSearchPersons";

export type InviteSearchPerson = {
  primaryCalendarId: string;
  profile: Profile;
  isUser: boolean;
  suggested?: boolean;
  pending?: boolean;
};

const Results = ({
  results,
  onCheck,
  selectedUsers,
}: {
  results: InviteSearchPerson[];
  onCheck: (selectedPerson: InviteSearchPerson, checked: boolean) => void;
  selectedUsers: InviteSearchPerson[];
}) => {
  return (
    <>
      {results.map((result) => {
        const isUserSelected = selectedUsers.some(
          (user) => user.primaryCalendarId === result.primaryCalendarId,
        );
        return (
          <div key={result.primaryCalendarId} className="cw-my-1">
            <InviteSearchResultRow
              searchPerson={result}
              onCheck={onCheck}
              isChecked={isUserSelected}
            />
          </div>
        );
      })}
    </>
  );
};

const BulkInvites = ({
  bulkInviteEvents,
  onUpdateSelectedBulkEvents,
  selectedEvents,
  selectedUsers,
}: {
  bulkInviteEvents: BulkInviteEventMap;
  onUpdateSelectedBulkEvents: (
    selectedPersons: InviteSearchPerson[],
    eventKey: string,
    checked: boolean,
  ) => void;
  selectedEvents: string[];
  selectedUsers: InviteSearchPerson[];
}) => {
  const areAllUsersSelected = (arr: InviteSearchPerson[], target: InviteSearchPerson[]) =>
    target.every((v) => arr.some((elem) => elem.primaryCalendarId === v.primaryCalendarId));
  const areSomeUsersSelected = (arr: InviteSearchPerson[], target: InviteSearchPerson[]) =>
    target.some((v) => arr.some((elem) => elem.primaryCalendarId === v.primaryCalendarId));
  return (
    <>
      {Object.keys(bulkInviteEvents).map((eventKey) => {
        const allUsersSelected = areAllUsersSelected(
          selectedUsers,
          bulkInviteEvents[eventKey].attendees,
        );
        const someUsersSelected = areSomeUsersSelected(
          bulkInviteEvents[eventKey].attendees,
          selectedUsers,
        );

        const isSelected = selectedEvents.includes(eventKey) && someUsersSelected;
        const isIndeterminate = !allUsersSelected && isSelected;
        return (
          <div key={eventKey} className="cw-my-1">
            <BulkInviteResultRow
              title={bulkInviteEvents[eventKey].title}
              attendees={bulkInviteEvents[eventKey].attendees}
              onCheck={onUpdateSelectedBulkEvents}
              isChecked={isSelected}
              eventKey={eventKey}
              isIndeterminate={isIndeterminate}
            />
          </div>
        );
      })}
      <div className="cw-border-b-[0.5px] cw-border-solid cw-border-neutral cw-mx-[20px]" />
    </>
  );
};

export const InviteSearch = ({
  suggestions,
  onCheck,
  bulkInviteEvents,
  trackingVariant,
}: {
  suggestions: InviteSearchPerson[];
  onCheck: (calendarIds: string[], checked: boolean, bulkInviteEvent?: BulkInviteEvent) => void;
  bulkInviteEvents: BulkInviteEventMap;
  trackingVariant: string;
}) => {
  const [selectedEvents, setSelectedEvents] = useState<Set<string>>(new Set([])); // list of selected eventKeys
  const [selectedUsers, setSelectedUsers] = useState<InviteSearchPerson[]>([]); // should be list of calendar ids
  const [searchQuery, setSearchQuery] = useState("");
  const suggestionIds = suggestions.map((suggestion) => suggestion.primaryCalendarId);
  const [loading, setLoading] = useState(false); // used to set loading immediatley after user types (instead of 250ms debounce delay)
  const [showBulkInvites, setShowBulkInvites] = useState(false);
  const { results, loading: queryLoading, search } = useInviteSearch(suggestionIds);

  // Tracking
  useEffect(() => {
    const suggestedInvites = suggestions.filter((suggestion) => suggestion.suggested === true); // some are just non users
    track(TrackingEvents.INVITE_SEARCH.INVITE_SUGGESTIONS_SEEN, {
      numSuggestions: suggestedInvites.length,
      suggestions: suggestedInvites,
      variant: trackingVariant,
    });
    track(TrackingEvents.INVITE_SEARCH.INVITE_BULK_SUGGESTIONS_SEEN, {
      numSuggestions: Object.keys(bulkInviteEvents).length,
      suggestions: bulkInviteEvents,
      variant: trackingVariant,
    });
  }, []);

  const displayUsers = useMemo(() => {
    if (!isEmpty(searchQuery) && !(queryLoading || loading)) {
      setShowBulkInvites(false);
      return results;
    } else if (queryLoading || loading) {
      setShowBulkInvites(false);
      return [];
    } else {
      setShowBulkInvites(true);
      return dedupeInviteSearchPersons(selectedUsers, suggestions);
    }
  }, [results, searchQuery, queryLoading]);

  useEffect(() => {
    if (!queryLoading) {
      setLoading(false);
    }
  }, [queryLoading, results]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (value.length > 0) {
      setLoading(true);
      search({ variables: { queryInput: value } });
    }
  };

  const onUpdateSelectedUsers = (selectedPerson: InviteSearchPerson, checked: boolean) => {
    onCheck([selectedPerson.primaryCalendarId], checked);
    let newPersons = [...selectedUsers];

    if (checked) {
      if (!newPersons.some((user) => user.primaryCalendarId === selectedPerson.primaryCalendarId)) {
        newPersons.push(selectedPerson);
      }
    } else {
      const newPerson = newPersons.find(
        (newPerson) => newPerson.primaryCalendarId === selectedPerson.primaryCalendarId,
      );
      if (newPerson) {
        const index = newPersons.indexOf(newPerson);
        newPersons.splice(index, 1);
      }
    }

    setSelectedUsers(newPersons);
  };

  const onUpdateSelectedBulkEvents = (
    selectedPersons: InviteSearchPerson[],
    eventKey: string,
    checked: boolean,
  ) => {
    const clonedEvents = cloneDeep(selectedEvents);

    let newEvents = new Set(clonedEvents);
    if (checked) {
      newEvents.add(eventKey);
    } else {
      newEvents.delete(eventKey);
    }
    setSelectedEvents(newEvents);

    let newPersons = [...selectedUsers];
    selectedPersons.forEach((selectedPerson) => {
      if (checked) {
        if (
          !newPersons.some((user) => user.primaryCalendarId === selectedPerson.primaryCalendarId)
        ) {
          newPersons.push(selectedPerson);
        }
      } else {
        const newPerson = newPersons.find(
          (newPerson) => newPerson.primaryCalendarId === selectedPerson.primaryCalendarId,
        );
        if (newPerson) {
          const index = newPersons.indexOf(newPerson);
          newPersons.splice(index, 1);
        }
      }
    });
    setSelectedUsers(newPersons);

    const caledarIdsFromSelectedPersons = selectedPersons.map((person) => person.primaryCalendarId);

    onCheck(caledarIdsFromSelectedPersons, checked, bulkInviteEvents[eventKey]);
  };

  const noResults = !(loading || queryLoading) && isEmpty(displayUsers);
  const shouldShowBulkInvites = showBulkInvites && !isEmpty(bulkInviteEvents);

  return (
    <div className="cw-w-full cw-flex-col">
      <div className="cw-pr-2">
        <TextField
          placeholder="Search for teammates"
          startIcon={Search}
          value={searchQuery}
          onChange={onInputChange}
          autoFocus={true}
          loading={queryLoading || loading}
          fullWidth
        />
      </div>
      {noResults && (
        <div className="cw-flex cw-justify-center cw-py-4 cw-text-muted"> No results found </div>
      )}
      <div className="cw-mb-2 cw-pt-2 cw-max-h-[375px] cw-overflow-scroll">
        {shouldShowBulkInvites && (
          <BulkInvites
            bulkInviteEvents={bulkInviteEvents}
            onUpdateSelectedBulkEvents={onUpdateSelectedBulkEvents}
            selectedEvents={Array.from(selectedEvents)}
            selectedUsers={selectedUsers}
          />
        )}
        <Results
          results={displayUsers}
          onCheck={onUpdateSelectedUsers}
          selectedUsers={selectedUsers}
        />
      </div>
    </div>
  );
};
